import React, { useEffect, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHead,
  BlockTitle,
  BlockHeadContent,
  Icon,
  BlockBetween,
} from "../../components/Component";
import BasePaginationTable from "../../components/table/BasePaginationTable";
import { defaultParams } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteSuccess as deleteBrandingSubmissionSuccess,
  getListBrandingSubmission,
} from "../../slice/branding-submissions";
import { deleteBrandingSubmission } from "../../api/branding-submission";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../admin/modals/DeleteConfimationModal";

const truncate = (text, max = 80) => {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

function getColumns(onView, onDelete) {
  return [
    {
      head: <span className="sub-text">Submitter</span>,
      cell: (row) => {
        const primary =
          row?.createdBy?.businessName ?? row?.createdBy?.fullName ?? "Guest";
        const email = row?.createdBy?.email;
        const avatar = row?.createdBy?.avatar;
        const initial = primary?.[0]?.toUpperCase() ?? "?";
        return (
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            {avatar ? (
              <img
                src={avatar}
                alt="avatar"
                width={36}
                height={36}
                style={{
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #eee",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center text-muted"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#f5f6fa",
                  border: "1px solid #eee",
                  flexShrink: 0,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {initial}
              </div>
            )}
            <div className="d-flex flex-column" style={{ minWidth: 0 }}>
              <span className="text-truncate">{primary}</span>
              {email && (
                <span
                  className="text-muted text-truncate"
                  style={{ fontSize: 12 }}
                >
                  {email}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      head: <span className="sub-text">Looking for</span>,
      cell: (row) => <span>{row?.lookingFor ?? "N/A"}</span>,
    },
    {
      head: <span className="sub-text">Budget</span>,
      cell: (row) => <span>{row?.budget ?? "N/A"}</span>,
    },
    {
      head: <span className="sub-text">Deadline</span>,
      cell: (row) => (
        <span>
          {row?.deadline ? moment(row.deadline).format("DD/MM/YYYY") : "N/A"}
        </span>
      ),
    },
    {
      head: <span className="sub-text">Business</span>,
      cell: (row) => (
        <span title={row?.businessDesc}>{truncate(row?.businessDesc)}</span>
      ),
    },
    {
      head: <span className="sub-text">Created at</span>,
      cell: (row) => (
        <span>
          {row?.createdAt
            ? moment(row.createdAt).format("DD/MM/YYYY HH:mm")
            : "N/A"}
        </span>
      ),
    },
    {
      head: <span className="sub-text"></span>,
      cell: (row) => (
        <div className="d-flex align-items-center" style={{ gap: 12 }}>
          <a
            href="#detail"
            className="link link-light"
            onClick={(ev) => {
              ev.preventDefault();
              onView(row);
            }}
          >
            <Icon name="eye"></Icon>
            <span>View</span>
          </a>
          <a
            href="#delete"
            className="link link-danger"
            onClick={(ev) => {
              ev.preventDefault();
              onDelete(row);
            }}
            title="Delete"
          >
            <Icon name="trash"></Icon>
          </a>
        </div>
      ),
    },
  ];
}

const BrandingSubmissionsPage = () => {
  const { list, summary, loading } = useSelector(
    (state) => state.brandingSubmissions
  );
  const [params, setParams] = useState(defaultParams);
  const [pendingDelete, setPendingDelete] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getListBrandingSubmission(params));
  }, [dispatch, params]);

  const handlePageChange = (page) => {
    setParams({ ...params, page });
  };

  const onView = (row) => {
    navigate(`/branding-submissions/${row?._id}`);
  };

  const onDelete = (row) => {
    setPendingDelete(row);
  };

  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target?._id) return;
    try {
      await deleteBrandingSubmission(target._id);
      dispatch(deleteBrandingSubmissionSuccess(target._id));
      toast.success("Branding submission deleted");
    } catch (error) {
      toast.error(error?.message ?? "Failed to delete submission");
    }
  };

  const handleSearch = debounce((value) => {
    setParams((prev) => ({ ...prev, page: 1, keyword: value }));
  }, 400);

  const dataTableColumns = getColumns(onView, onDelete);

  return (
    <React.Fragment>
      <Head title="Branding Submissions"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle>Branding Submissions</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="form-control-wrap" style={{ minWidth: 260 }}>
                <div className="form-icon form-icon-right">
                  <Icon name="search"></Icon>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search submissions"
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <BasePaginationTable
          list={list}
          loading={loading}
          columns={dataTableColumns}
          onPageChange={handlePageChange}
          onRowClick={onView}
          hasNextPage={summary?.hasNextPage}
          totalItems={summary?.count}
          {...params}
        />
      </Content>
      <DeleteConfirmationModal
        isOpen={pendingDelete != null}
        onCloseModal={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete branding submission?"
        desc="The submission and all its data will be permanently deleted."
      />
    </React.Fragment>
  );
};

export default BrandingSubmissionsPage;
